/**
 * Phase 127 — Public venue discovery + booking page.
 *
 * URL: /arenax/venues/[slug] (or by id)
 *
 * Public surface — anyone can browse.  Auth is required only to request
 * a slot.  Closes the demand half of the city sports operating loop:
 *
 *   Player browses → requests slot → operator accepts → player pays → confirmed
 *
 * Visual language: minimal supply-side trust UI.  Hero image, sport
 * chips, amenity strip, slot grid in 1-hr blocks, "Request slot" CTA.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxCityPulseTicker from './ArenaxCityPulseTicker';
import ArenaxPaymentModeBanner from './ArenaxPaymentModeBanner';
import { resolveSrc } from './ArenaxImageUploader';
import {
  MapPin, Loader2, Lightbulb, Sun, Calendar, ChevronLeft, ChevronRight,
  ArrowRight, Clock, CheckCircle2, X, IndianRupee,
} from 'lucide-react';

const STATE_STYLES = {
  available: { bg: 'var(--ax-card-bg-soft)', fg: 'hsl(var(--ax-text))', border: 'var(--ax-border-quiet)', clickable: true },
  requested: { bg: 'rgba(245,158,11,0.18)', fg: '#92400e', border: 'rgba(245,158,11,0.35)' },
  accepted:  { bg: 'rgba(245,158,11,0.18)', fg: '#92400e', border: 'rgba(245,158,11,0.35)' },
  confirmed: { bg: 'rgba(16,185,129,0.20)', fg: '#047857', border: 'rgba(16,185,129,0.40)' },
  live:      { bg: 'rgba(6,182,212,0.30)', fg: '#0e7490', border: 'rgba(6,182,212,0.55)' },
};

function fmtDate(d) { return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
function fmtTime(s) { return new Date(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
function dateOnly(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

export default function ArenaxVenuePublic() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [weekStart, setWeekStart] = useState(() => dateOnly(new Date()));
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const start = weekStart.toISOString();
      const end = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const r = await api.get(`/arenax/venues/${encodeURIComponent(slug)}/slots?start=${start}&end=${end}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Venue not found');
      setData(null);
    } finally { setLoading(false); }
  };

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  // Group slots by court_id then by day → for the per-court row layout
  const slotsByCourtByDay = useMemo(() => {
    const map = new Map();
    if (!data?.slots) return map;
    for (const s of data.slots) {
      if (!map.has(s.court_id)) map.set(s.court_id, new Map());
      const d = new Date(s.start_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const inner = map.get(s.court_id);
      if (!inner.has(key)) inner.set(key, []);
      inner.get(key).push(s);
    }
    return map;
  }, [data]);

  const requestSlot = async (slot) => {
    if (!user) {
      toast.message('Sign in to request this slot');
      navigate(`/arenax/auth/login?return_to=/arenax/venues/${slug}`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/arenax/venues/${encodeURIComponent(slug)}/slots/${slot.id}/request`, {
        party_size: 1,
      });
      toast.success(`Slot requested — operator has ${Math.ceil((new Date(r.data.lock_expires_at) - new Date()) / 60000)} min to accept`);
      setSelected(null);
      await load();
      navigate('/arenax/me/bookings');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not request slot');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <ArenaxLayout>
        <div className="flex justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
        </div>
      </ArenaxLayout>
    );
  }
  if (!data) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <MapPin className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-text-faint))' }} />
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Venue not found
          </h1>
          <button onClick={() => navigate('/arenax/venues')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}>
            Browse venues
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  const venue = data.venue;
  const cover = resolveSrc(venue.cover_image ? { url: venue.cover_image, path: venue.cover_image } : null);

  return (
    <ArenaxLayout>
      <section className="max-w-5xl mx-auto px-5 py-6 arenax-themed space-y-6" data-testid="arenax-venue-public-page">

        {/* Hero */}
        <div className="rounded-2xl overflow-hidden relative" style={{ minHeight: 220 }}>
          {cover ? (
            <img src={cover} alt={venue.name} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0" style={{ background: 'var(--ax-bg-gradient)' }} />
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.65), rgba(0,0,0,0.05) 60%)' }} />
          <div className="relative px-6 py-8 sm:py-12 flex flex-col justify-end" style={{ minHeight: 220 }}>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2 text-white/80">
              {venue.entity_type?.replace('_', ' ') || 'Venue'}
            </div>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-white mb-1"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {venue.name}
            </h1>
            {venue.tagline && (
              <div className="text-sm italic text-white/85 mb-2">"{venue.tagline}"</div>
            )}
            <div className="flex items-center gap-3 text-xs text-white/85">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {venue.area || venue.city}</span>
              {venue.lighting === 'floodlit' && <span className="inline-flex items-center gap-1"><Lightbulb className="h-3 w-3" /> Floodlit</span>}
              {venue.lighting === 'natural' && <span className="inline-flex items-center gap-1"><Sun className="h-3 w-3" /> Daylight</span>}
              {venue.surface_type && <span>· {venue.surface_type}</span>}
            </div>
          </div>
        </div>

        {/* Sports + amenities strip */}
        <div className="flex flex-wrap items-center gap-2" data-testid="venue-public-sports">
          {(venue.sports_supported || []).map(s => (
            <span key={s} className="arenax-chip text-[11px] px-3 py-1.5 rounded-full">{s}</span>
          ))}
          {(venue.amenities || []).slice(0, 6).map(a => (
            <span key={a} className="text-[10px] px-2.5 py-1 rounded-full"
                  style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}>
              {a}
            </span>
          ))}
        </div>

        {/* Phase 128 — city pulse strip for ambient context */}
        <ArenaxCityPulseTicker city={venue.city} maxItems={8} />

        {/* Week navigator */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
                    className="h-8 px-2 rounded-lg text-xs flex items-center gap-1"
                    style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}
                    data-testid="venue-public-prev-week"
            ><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
            <div className="text-sm font-bold tracking-tight" style={{ color: 'hsl(var(--ax-text))' }}>
              {fmtDate(days[0])} — {fmtDate(days[6])}
            </div>
            <button onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
                    className="h-8 px-2 rounded-lg text-xs flex items-center gap-1"
                    style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}
                    data-testid="venue-public-next-week"
            >Next <ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
               style={{ color: 'hsl(var(--ax-text-faint))' }}>
            <Calendar className="h-3 w-3" /> Tap a slot to request
          </div>
        </div>

        {/* Court rows */}
        <div className="space-y-6" data-testid="venue-public-court-list">
          {(data.courts || []).map(court => (
            <div key={court.id} className="arenax-card p-5 rounded-2xl"
                 data-testid={`venue-public-court-${court.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-bold" style={{ color: 'hsl(var(--ax-text))' }}>{court.name}</div>
                  <div className="text-[11px]" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                    {court.sport || '—'} {court.indoor ? '· Indoor' : ''}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {days.map((d, di) => {
                  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const daySlots = slotsByCourtByDay.get(court.id)?.get(key) || [];
                  const isToday = dateOnly(d).getTime() === dateOnly(new Date()).getTime();
                  return (
                    <div key={di} className="rounded-lg overflow-hidden"
                         style={{ background: 'var(--ax-card-bg-soft)', border: '1px solid var(--ax-border-quiet)' }}>
                      <div className="px-2 py-1 text-[9px] uppercase tracking-wider font-bold text-center"
                           style={{
                             color: isToday ? 'hsl(var(--ax-accent-cyan))' : 'hsl(var(--ax-text-faint))',
                             borderBottom: '1px solid var(--ax-border-quiet)',
                           }}>
                        {fmtDate(d).replace(',', '')}
                      </div>
                      <div className="px-1 py-1 max-h-60 overflow-y-auto space-y-1">
                        {daySlots.length === 0 && (
                          <div className="text-[9px] py-2 text-center italic"
                               style={{ color: 'hsl(var(--ax-text-faint))' }}>—</div>
                        )}
                        {daySlots.map(s => {
                          const style = STATE_STYLES[s.state] || STATE_STYLES.available;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => style.clickable && setSelected(s)}
                              disabled={!style.clickable}
                              className="w-full text-[10px] font-bold py-1.5 rounded transition-all hover:-translate-y-0.5 disabled:cursor-default disabled:hover:translate-y-0"
                              style={{
                                background: style.bg,
                                color: style.fg,
                                border: `1px solid ${style.border}`,
                                boxShadow: s.is_peak ? 'inset 0 0 0 1px rgba(217,119,6,0.45)' : 'none',
                              }}
                              data-testid={`venue-public-slot-${s.id}`}
                            >
                              {fmtTime(s.start_at)} {s.price ? `· ₹${s.price}` : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {(data.courts || []).length === 0 && (
            <div className="arenax-card p-10 text-center rounded-2xl">
              <Calendar className="h-8 w-8 mx-auto mb-2" style={{ color: 'hsl(var(--ax-text-faint))' }} />
              <p className="text-sm" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                This venue hasn't set up courts yet. Check back soon.
              </p>
            </div>
          )}
        </div>

        {/* Confirm slot modal */}
        {selected && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-5 arenax-themed"
               style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
               data-testid="venue-public-confirm-modal">
            <div className="arenax-card p-6 rounded-2xl max-w-md w-full">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1"
                       style={{ color: 'hsl(var(--ax-text-faint))' }}>
                    Request this slot
                  </div>
                  <h3 className="text-lg font-bold" style={{ color: 'hsl(var(--ax-text))' }}>
                    {data.courts.find(c => c.id === selected.court_id)?.name} · {fmtDate(new Date(selected.start_at))}
                  </h3>
                  <div className="text-sm flex items-center gap-3 mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />
                      {fmtTime(selected.start_at)} – {fmtTime(selected.end_at)}
                    </span>
                    {selected.price ? (
                      <span className="inline-flex items-center gap-0.5 font-bold"
                            style={{ color: 'hsl(var(--ax-text))' }}>
                        <IndianRupee className="h-3 w-3" />{selected.price}
                      </span>
                    ) : null}
                    {selected.is_peak && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(217,119,6,0.15)', color: '#92400e' }}>Peak</span>
                    )}
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="h-7 w-7 rounded-full flex items-center justify-center"
                        style={{ color: 'hsl(var(--ax-text-muted))' }}>
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs leading-relaxed mb-4"
                 style={{ color: 'hsl(var(--ax-text-muted))' }}>
                Operator will review your request within ~15 minutes.  Once they accept,
                you'll get a payment link.  Lock it → your ground is set.
              </p>
              <div className="mb-4">
                <ArenaxPaymentModeBanner variant="strip" hideWhenLive testId="venue-checkout-payment-mode" />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setSelected(null)}
                        className="text-xs px-3 py-2 rounded-lg"
                        style={{ color: 'hsl(var(--ax-text-muted))' }}>
                  Cancel
                </button>
                <button onClick={() => requestSlot(selected)}
                        disabled={busy}
                        className="text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                        style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))', boxShadow: 'var(--ax-shadow-glow)' }}
                        data-testid="venue-public-confirm-request">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Request slot <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </ArenaxLayout>
  );
}
